import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EntityFormModal = ({ isOpen, onClose, onSave, item, mode, entityType, config, language }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState([]);

  useEffect(() => {
    if (item) {
      setFormData({ ...item });
      if (item?.images) {
        setImagePreview(item?.images);
      }
    } else {
      const initialData = {};
      config?.formFields?.forEach(field => {
        if (field?.key?.includes('.')) {
          const [parent, child] = field?.key?.split('.');
          if (!initialData?.[parent]) initialData[parent] = {};
          initialData[parent][child] = '';
        } else {
          initialData[field.key] = field?.type === 'multiselect' ? [] : '';
        }
      });
      setFormData(initialData);
      setImagePreview([]);
    }
    setErrors({});
  }, [item, isOpen, config]);

  const labels = {
    addTitle: { en: `Add New ${config?.labels?.singular?.en}`, ar: `إضافة ${config?.labels?.singular?.ar} جديد` },
    editTitle: { en: `Edit ${config?.labels?.singular?.en}`, ar: `تعديل ${config?.labels?.singular?.ar}` },
    cancel: { en: 'Cancel', ar: 'إلغاء' },
    save: { en: 'Save', ar: 'حفظ' },
    uploadImages: { en: 'Upload Images', ar: 'تحميل الصور' },
    required: { en: 'This field is required', ar: 'هذا الحقل مطلوب' },
    invalidPhone: { en: 'Invalid phone number format', ar: 'تنسيق رقم الهاتف غير صحيح' },
    invalidCoordinates: { en: 'Invalid GPS coordinates', ar: 'إحداثيات GPS غير صحيحة' },
    invalidPrice: { en: 'Invalid price', ar: 'سعر غير صحيح' },
    invalidYear: { en: 'Year must be between 1900 and 2100', ar: 'يجب أن تكون السنة بين 1900 و 2100' }
  };

  const validateForm = () => {
    const newErrors = {};

    config?.formFields?.forEach(field => {
      const value = field?.key?.includes('.')
        ? field?.key?.split('.')?.reduce((obj, key) => obj?.[key], formData)
        : formData?.[field?.key];

      if (field?.validation) {
        const error = field?.validation(value, formData);
        if (error) {
          newErrors[field.key] = labels?.[error] || labels?.required;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleChange = (fieldKey, value) => {
    if (fieldKey?.includes('.')) {
      const [parent, child] = fieldKey?.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev?.[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [fieldKey]: value }));
    }
    if (errors?.[fieldKey]) {
      setErrors(prev => ({ ...prev, [fieldKey]: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e?.target?.files);
    const imageUrls = files?.map(file => URL.createObjectURL(file));
    setImagePreview([...imagePreview, ...imageUrls]);
    setFormData(prev => ({ ...prev, images: [...(prev?.images || []), ...imageUrls] }));
  };

  const removeImage = (index) => {
    const newImages = imagePreview?.filter((_, i) => i !== index);
    setImagePreview(newImages);
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const renderField = (field) => {
    const value = field?.key?.includes('.')
      ? field?.key?.split('.')?.reduce((obj, key) => obj?.[key], formData)
      : formData?.[field?.key];

    switch (field?.type) {
      case 'text': case'number':
        return (
          <Input
            key={field?.key}
            type={field?.type}
            label={field?.label?.[language]}
            value={value || ''}
            onChange={(e) => handleChange(field?.key, e?.target?.value)}
            error={errors?.[field?.key]}
            required={field?.required}
          />
        );

      case 'textarea':
        return (
          <div key={field?.key} className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {field?.label?.[language]}
              {field?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <textarea
              value={value || ''}
              onChange={(e) => handleChange(field?.key, e?.target?.value)}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors?.[field?.key] && (
              <p className="text-sm text-destructive">{errors?.[field?.key]}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <Select
            key={field?.key}
            label={field?.label?.[language]}
            options={field?.options?.map(opt => ({
              value: opt?.value,
              label: opt?.label?.[language]
            }))}
            value={value || ''}
            onChange={(val) => handleChange(field?.key, val)}
            error={errors?.[field?.key]}
            required={field?.required}
          />
        );

      case 'multiselect':
        return (
          <Select
            key={field?.key}
            label={field?.label?.[language]}
            options={field?.options?.map(opt => ({
              value: opt?.value,
              label: opt?.label?.[language]
            }))}
            value={value || []}
            onChange={(val) => handleChange(field?.key, val)}
            error={errors?.[field?.key]}
            required={field?.required}
            multiple
            searchable={true}
          />
        );

      case 'file':
        return (
          <div key={field?.key} className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {field?.label?.[language]}
            </label>
            <div className="flex flex-col gap-3">
              <input
                type="file"
                multiple={field?.multiple}
                accept="image/*"
                onChange={handleImageUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
              {imagePreview?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {imagePreview?.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-border"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 p-1 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {mode === 'add' ? labels?.addTitle?.[language] : labels?.editTitle?.[language]}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {config?.formFields?.map(field => renderField(field))}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              {labels?.cancel?.[language]}
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
            >
              {labels?.save?.[language]}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntityFormModal;